import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateOrderingSettings } from "@/hooks/use-orders";
import { saveDeliveryPricing } from "../delivery-pricing-model";

type DeliveryPricingCardProps = {
  deliveryEnabled: boolean;
  deliveryFeeFlat: number;
  deliveryMinimumOrder: number;
};

/**
 * Issue #90: the write surface for the Organization's flat Delivery fee and
 * Delivery minimum order. Both values were already read at Delivery Checkout —
 * the fee lands on the Order total and the Order payment preference, and the
 * minimum blocks submission — but nothing could write them, so every
 * Organization was pinned at 0. Owner/admin only: the route is wrapped in
 * `OrgAdminRoute` and the PATCH handler runs `requireOrgAdmin`.
 */
export function DeliveryPricingCard({
  deliveryEnabled,
  deliveryFeeFlat,
  deliveryMinimumOrder,
}: DeliveryPricingCardProps) {
  const { t } = useTranslation();
  const update = useUpdateOrderingSettings();
  const [feeText, setFeeText] = useState(deliveryFeeFlat.toString());
  const [minimumOrderText, setMinimumOrderText] = useState(deliveryMinimumOrder.toString());

  useEffect(() => {
    setFeeText(deliveryFeeFlat.toString());
    setMinimumOrderText(deliveryMinimumOrder.toString());
  }, [deliveryFeeFlat, deliveryMinimumOrder]);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await saveDeliveryPricing(
      { feeText, minimumOrderText },
      {
        onError: (err) => toast.error(err instanceof Error ? err.message : "Error"),
        onInvalid: () =>
          toast.error(
            t("orders.settings.deliveryPricingInvalid", {
              defaultValue:
                "El costo de envío y el pedido mínimo deben ser números enteros iguales o mayores a 0.",
            }),
          ),
        onSaved: () =>
          toast.success(
            t("orders.settings.deliveryPricingSaved", {
              defaultValue: "Costo de envío y pedido mínimo guardados",
            }),
          ),
        update: update.mutateAsync,
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("orders.settings.deliveryPricingTitle", {
            defaultValue: "Costo de envío y pedido mínimo",
          })}
        </CardTitle>
        <CardDescription>
          {deliveryEnabled
            ? t("orders.settings.deliveryPricingDescription", {
                defaultValue:
                  "El costo de envío se suma al total del pedido y al cobro online. Por debajo del pedido mínimo, el cliente no puede confirmar el envío. Dejalo en 0 para no cobrar envío ni exigir un mínimo.",
              })
            : t("orders.settings.deliveryPricingRequiresDelivery", {
                defaultValue:
                  "Habilitá los envíos para definir el costo de envío y el pedido mínimo.",
              })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={submit}>
          <fieldset className="space-y-3" disabled={!deliveryEnabled || update.isPending}>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="deliveryFeeFlat">
                  {t("orders.settings.deliveryFeeFlat", {
                    defaultValue: "Costo de envío (fijo)",
                  })}
                </Label>
                <Input
                  id="deliveryFeeFlat"
                  inputMode="numeric"
                  min={0}
                  onChange={(event) => setFeeText(event.target.value)}
                  placeholder="0"
                  value={feeText}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryMinimumOrder">
                  {t("orders.settings.deliveryMinimumOrder", {
                    defaultValue: "Pedido mínimo para envío",
                  })}
                </Label>
                <Input
                  id="deliveryMinimumOrder"
                  inputMode="numeric"
                  min={0}
                  onChange={(event) => setMinimumOrderText(event.target.value)}
                  placeholder="0"
                  value={minimumOrderText}
                />
              </div>
            </div>

            <Button disabled={update.isPending} type="submit">
              {t("orders.settings.deliveryPricingSave", {
                defaultValue: "Guardar costo y mínimo",
              })}
            </Button>
          </fieldset>
        </form>
      </CardContent>
    </Card>
  );
}
